// src/services/agentService.ts
import axios from "axios"
import { supabase } from "../supabase.ts"

/** Axios instance pointing to our server API */
const api = axios.create({
  baseURL: "/api",
  headers: {
    "Content-Type": "application/json",
  },
})

// Add a request interceptor to include the user ID in the headers
api.interceptors.request.use(
  async (config) => {
    // Get the current session
    const {
      data: { session },
    } = await supabase.auth.getSession()

    // If there's a session, add the user ID to the headers
    if (session) {
      config.headers["user-id"] = session.user.id
    }

    return config
  },
  (error) => {
    return Promise.reject(error)
  },
)

// Update the Agent interface to include llm_websocket_url
export interface Agent {
  agent_id: string
  agent_name: string | null
  last_modification_timestamp: number
  response_engine: {
    type: "retell-llm" | "custom-llm"
    llm_id: string
    general_tools?: any[] | null
    llm_websocket_url?: string
  }
  voice_id: string
  voice_model: string | null
  language: string
  webhook_url?: string | null
  opt_out_sensitive_data_storage?: boolean
  fallback_voice_ids?: string[] | null
  post_call_analysis_data?: Array<{
    type: "string" | "enum" | "boolean" | "number"
    name: string
    description: string
    examples?: string[]
    choices?: string[]
  }> | null
  enable_voicemail_detection?: boolean
  end_call_after_silence_ms?: number
  max_call_duration_ms?: number
  begin_message_delay_ms?: number
  voicemail_message?: string
  voicemail_detection_timeout_ms?: number
}

// Update the CreateAgentParams interface
export interface CreateAgentParams {
  agent_name?: string
  response_engine: {
    type: "retell-llm" | "custom-llm"
    llm_id: string
    llm_websocket_url?: string
  }
  voice_id: string
  voice_model?: string | null
  language?: string
  webhook_url?: string | null
  voicemail_message?: string
  voicemail_detection_timeout_ms?: number
}

// Update the UpdateAgentParams interface
export interface UpdateAgentParams {
  agent_name?: string | null
  response_engine?: {
    type: "retell-llm" | "custom-llm"
    llm_id: string
    llm_websocket_url?: string
  }
  voice_id?: string
  voice_model?: string | null
  language?: string
  voice_temperature?: number
  voice_speed?: number
  webhook_url?: string | null
  opt_out_sensitive_data_storage?: boolean
  fallback_voice_ids?: string[] | null
  post_call_analysis_data?: Array<{
    type: "string" | "enum" | "boolean" | "number"
    name: string
    description: string
    examples?: string[]
    choices?: string[]
  }> | null
  enable_voicemail_detection?: boolean
  end_call_after_silence_ms?: number
  max_call_duration_ms?: number
  begin_message_delay_ms?: number
  voicemail_message?: string
  voicemail_detection_timeout_ms?: number
}

export interface VoiceDetails {
  voice_id: string
  voice_type: string
  voice_name: string
  provider: "elevenlabs" | "openai" | "deepgram" | "play" | "playht"
  gender: "male" | "female"
  accent?: string
  age?: string
  preview_audio_url?: string
  avatar_url?: string
}

/** Text models (Retell `NullableLLMModel` minus null). */
export type RetellTextLlmModel =
  | "gpt-4.1"
  | "gpt-4.1-mini"
  | "gpt-4.1-nano"
  | "gpt-5"
  | "gpt-5-mini"
  | "gpt-5-nano"
  | "gpt-5.1"
  | "gpt-5.2"
  | "gpt-5.4"
  | "gpt-5.4-mini"
  | "gpt-5.4-nano"
  | "gpt-5.5"
  | "claude-4.5-sonnet"
  | "claude-4.6-sonnet"
  | "claude-4.5-haiku"
  | "gemini-2.5-flash-lite"
  | "gemini-3.0-flash"
  | "gemini-3.1-flash-lite"

/** Speech-to-speech models (mutually exclusive with `model` on create/update). */
export type RetellS2sLlmModel =
  | "gpt-realtime-2"
  | "gpt-realtime-1.5"
  | "gpt-realtime"
  | "gpt-realtime-mini"

export const DEFAULT_RETELL_TEXT_MODEL: RetellTextLlmModel = "gpt-5.5"

export interface RetellLLM {
  llm_id: string
  last_modification_timestamp: number
  /** API may return legacy ids; keep loose for display. */
  model: string | null
  s2s_model: string | null
  model_temperature?: number
  model_high_priority?: boolean
  tool_call_strict_mode?: boolean
  general_prompt?: string | null
  general_tools?: any[] | null
  states?: any[] | null
  starting_state?: string | null
  begin_message?: string | null
  knowledge_base_ids?: string[] | null
  start_speaker?: "user" | "agent" | null
}

export interface CreateRetellLLMParams {
  model?: RetellTextLlmModel | null
  s2s_model?: RetellS2sLlmModel | null
  model_temperature?: number
  model_high_priority?: boolean
  tool_call_strict_mode?: boolean
  general_prompt?: string | null
  general_tools?: any[] | null
  states?: any[] | null
  starting_state?: string | null
  begin_message?: string | null
  knowledge_base_ids?: string[] | null
  start_speaker?: "user" | "agent"
}

export interface UpdateRetellLLMParams {
  /** Sent only to our backend; stripped before Retell. Resolves draft `version` from agent version history. */
  agent_id?: string
  model?: RetellTextLlmModel | null
  s2s_model?: RetellS2sLlmModel | null
  model_temperature?: number
  model_high_priority?: boolean
  tool_call_strict_mode?: boolean
  general_prompt?: string | null
  general_tools?: any[] | null
  states?: any[] | null
  starting_state?: string | null
  begin_message?: string | null
  knowledge_base_ids?: string[] | null
  start_speaker?: "user" | "agent"
}

/** Prefer server `hint` (e.g. NO_AGENT_DRAFT) for multi-line toast copy. */
export function formatApiUserMessage(
  error: unknown,
  fallback = "An unexpected error occurred"
): string {
  const ax = error as {
    response?: { data?: { message?: string; hint?: string } }
  }
  const d = ax.response?.data
  if (d?.message && d?.hint && typeof d.hint === "string") {
    return `${d.message}\n\n${d.hint}`
  }
  if (d?.message && typeof d.message === "string") return d.message
  return fallback
}

// Define the structure for calendar availability tool
export interface CalendarAvailabilityTool {
  type: "check_availability_cal"
  name: string
  description?: string
  cal_api_key: string
  event_type_id: number
  timezone?: string
}

// Define the union type for all possible tool types
export type GeneralTool = CalendarAvailabilityTool | any // 'any' for other tool types

export interface FunctionConfig {
  type: string
  name: string
  description?: string
  [key: string]: any
}

export interface LLMData {
  llm_id: string
  last_modification_timestamp: number
  model: string | null
  s2s_model: string | null
  model_temperature?: number
  model_high_priority?: boolean
  tool_call_strict_mode?: boolean
  general_prompt?: string | null
  general_tools?: any[] | null
  states?: any[] | null
  starting_state?: string | null
  begin_message?: string | null
  knowledge_base_ids?: string[] | null
}

/* ----------------------------------------------------------
 *      agentService with all endpoints
 * --------------------------------------------------------*/

export const agentService = {
  // ---------------- Agent Endpoints ----------------
  list: async () => {
    const response = await api.get<Agent[]>("/agents")
    return response.data
  },

  get: async (agentId: string) => {
    const response = await api.get<Agent>(`/agents/${agentId}`)
    return response.data
  },

  create: async (params: CreateAgentParams) => {
    const response = await api.post<Agent>("/agents", params)
    return response.data
  },

  delete: async (agentId: string) => {
    await api.delete(`/agents/${agentId}`)
  },

  update: async (agentId: string, params: UpdateAgentParams) => {
    const response = await api.patch<Agent>(`/agents/${agentId}`, params)
    return response.data
  },

  // ---------------- Voice Endpoints ----------------
  getVoiceDetails: async (voiceId: string) => {
    const response = await api.get<VoiceDetails>(`/voices/${voiceId}`)
    return response.data
  },

  listVoices: async () => {
    const response = await api.get<VoiceDetails[]>("/voices")
    return response.data
  },

  // ---------------- LLM Endpoints ----------------
  listRetellLLMs: async () => {
    const response = await api.get<RetellLLM[]>("/llms")
    return response.data
  },

  getRetellLLM: async (llmId: string) => {
    const response = await api.get<RetellLLM>(`/llms/${llmId}`)
    return response.data
  },

  createRetellLLM: async (params: CreateRetellLLMParams) => {
    const response = await api.post<RetellLLM>("/llms", params)
    return response.data
  },

  updateRetellLLM: async (llmId: string, params: UpdateRetellLLMParams) => {
    // Transform the data if general_tools is present
    if (params.general_tools && Array.isArray(params.general_tools)) {
      params.general_tools = params.general_tools.map((tool) => {
        // Handle calendar availability tool
        if (tool.type === "check_availability_cal") {
          // Convert cal_event_type_id to event_type_id if present
          if (tool.cal_event_type_id && !tool.event_type_id) {
            tool.event_type_id = Number(tool.cal_event_type_id)
            delete tool.cal_event_type_id
          }
          // Ensure event_type_id is a number
          if (typeof tool.event_type_id === "string") {
            tool.event_type_id = Number(tool.event_type_id)
          }
        }
        return tool
      })
    }

    const response = await api.patch<RetellLLM>(`/llms/${llmId}`, params)
    return response.data
  },
  getLLMData: async (llmId: string): Promise<RetellLLM> => {
    const response = await api.get<RetellLLM>(`/llm/${llmId}`)
    return response.data
  },

  getAgentDetails: async (agentId: string) => {
    const response = await api.get<Agent>(`/agents/${agentId}`)
    return response.data
  },

  updateAgentSettings: async (agentId: string, settings: Partial<Agent>) => {
    const response = await api.patch<Agent>(`/agents/${agentId}`, settings)
    return response.data
  },
}
